import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  alpha,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip,
  InputAdornment,
  Grid2,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import { useTheme } from "@mui/material/styles";
import { TablePaginationCustom } from "../Minimal/src/components/pagination";
import { useTable } from "../Minimal/src/hooks/use-table";
import { useDebounce } from "minimal-shared";
import Header from "../Header";
import Footer from "../Footer";
import config from "../common/Config";
import Select from "react-select";
import { format } from "date-fns";
import { toast } from "sonner";
import '../common/commonStyles.css';

const Catalogue = () => {
  const theme = useTheme();
  const table = useTable({
    defaultRowsPerPage: 10,
    defaultDense: false,
    defaultOrder: "desc",
    defaultOrderBy: "date",
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const [brandList, setBrandList] = useState([]);
  const [allBrandList, setAllBrandList] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productsSelected, setProductsSelected] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [productType, setProductType] = useState("");
  const [hideProducts, setHideProducts] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [catalogueDate, setCatalogueDate] = useState(new Date());
  const [packSizeEnabled, setPackSizeEnabled] = useState(true);
  const [progressOpen, setProgressOpen] = useState(false);
  const [createProgress, setCreateProgress] = useState({
    percent: 0,
    status: "",
    message: "",
    completedPart: 0,
    totalParts: 0,
    totalProducts: 0,
    refNum: "",
    brochureId: null,
    processing: false,
    pdfFile: null,
  });
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const authToken = token || accessToken;
  const progressPollRef = useRef(null);
  const progressAnimRef = useRef(null);
  const progressStartRef = useRef(null);
  const apiPercentRef = useRef(0);
  const activeBrochureIdRef = useRef(null);

  const CREATE_PROGRESS_MESSAGE =
    "We're creating your catalogue. It will be ready in just a few moments.";
  const ESTIMATED_PROGRESS_MS = 120000;
  const MAX_ESTIMATED_PERCENT = 95;

  const mapCreateProgress = (data = {}) => ({
    percent: Number(data.percent) || 0,
    status: String(data.status || "").toLowerCase(),
    message: data.message || "",
    completedPart: Number(data.completed_part) || 0,
    totalParts: Number(data.total_parts) || 0,
    totalProducts: Number(data.total_products) || 0,
    refNum: data.ref_num ? String(data.ref_num) : "",
    brochureId: data.brochure_id ?? null,
    processing:
      data.processing === true ||
      data.processing === 1 ||
      data.processing === "1" ||
      String(data.status || "").toLowerCase() === "processing",
    pdfFile: data.pdf_file || null,
  });

  const resolvePdfUrl = (pdfFile) => {
    if (!pdfFile) return null;
    if (/^https?:\/\//i.test(pdfFile)) return pdfFile;
    if (pdfFile.startsWith("/")) return `${config.API_URL}${pdfFile}`;
    return `${config.API_URL}/public/${pdfFile}`;
  };

  const stopProgressAnimation = useCallback(() => {
    if (progressAnimRef.current) {
      clearInterval(progressAnimRef.current);
      progressAnimRef.current = null;
    }
  }, []);

  const startProgressAnimation = useCallback(
    (reset = true) => {
      if (!reset && progressAnimRef.current) {
        return;
      }
      stopProgressAnimation();
      progressStartRef.current = Date.now();
      if (reset) {
        apiPercentRef.current = 0;
      }
      progressAnimRef.current = setInterval(() => {
        const elapsed = Date.now() - (progressStartRef.current || Date.now());
        const t = Math.min(1, elapsed / ESTIMATED_PROGRESS_MS);
        const eased = 1 - Math.pow(1 - t, 2.2);
        const estimated = Math.max(1, Math.floor(eased * MAX_ESTIMATED_PERCENT));
        const nextPercent = Math.min(
          MAX_ESTIMATED_PERCENT,
          Math.max(estimated, apiPercentRef.current || 0)
        );

        setCreateProgress((prev) => {
          if (String(prev.status).toLowerCase() === "completed") {
            return prev;
          }
          if (String(prev.status).toLowerCase() === "failed") {
            return prev;
          }
          return {
            ...prev,
            processing: true,
            percent: Math.max(prev.percent || 0, nextPercent),
            message: `${Math.max(prev.percent || 0, nextPercent)}% completed`,
          };
        });
      }, 300);
    },
    [stopProgressAnimation]
  );

  const stopProgressPolling = useCallback(() => {
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
      progressPollRef.current = null;
    }
    stopProgressAnimation();
  }, [stopProgressAnimation]);

  const parseRowDate = (rowDate) => {
    if (!rowDate) return null;
    const raw = String(rowDate).trim();
    if (!raw) return null;

    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const y = Number(isoMatch[1]);
      const m = Number(isoMatch[2]);
      const d = Number(isoMatch[3]);
      const dt = new Date(y, m - 1, d);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const dmyMatch = raw.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{2}|\d{4})$/);
    if (dmyMatch) {
      const d = Number(dmyMatch[1]);
      const m = Number(dmyMatch[2]);
      let y = Number(dmyMatch[3]);
      if (y < 100) y += 2000;
      const dt = new Date(y, m - 1, d);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const formatListDate = (rowDate) => {
    const dt = parseRowDate(rowDate);
    if (!dt) return rowDate || "-";
    return format(dt, "dd-MM-yyyy");
  };

  const isNotToday = (rowDate) => {
    const dt = parseRowDate(rowDate);
    if (!dt) return true;
    const formattedRowDate = format(dt, "yyyy-MM-dd");
    const today = new Date().toISOString().split("T")[0];
    return formattedRowDate !== today;
  };

  const fetchCatalogueList = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const brandForm = new FormData();
      brandForm.append("draw", 1);
      brandForm.append("start", table.page * table.rowsPerPage);
      brandForm.append("length", table.rowsPerPage);

      const apiBrandUrl = `${config.API_URL}/ws-catalog-list`;
      const response = await fetch(apiBrandUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${authToken}`,
        },
        body: brandForm,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch brand list");
      }

      const data = await response.json();
      const catalogueData = data.data || [];
      setAllBrandList(catalogueData);
      setBrandList(catalogueData);
    } catch (error) {
      toast.error("Failed to fetch catalogue list");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authToken, table.page, table.rowsPerPage]);

  const fetchCreateCatalogueProgress = useCallback(
    async (brochureId) => {
      const params = new URLSearchParams();
      if (brochureId != null && brochureId !== "") {
        params.set("brochure_id", String(brochureId));
      }
      const qs = params.toString();
      const url = qs
        ? `${config.API_URL}/create-catalogue_new?${qs}`
        : `${config.API_URL}/create-catalogue_new`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch catalogue progress");
      }

      return response.json();
    },
    [authToken]
  );

  const handleCreateProgressCompleted = useCallback(
    (data) => {
      stopProgressPolling();
      apiPercentRef.current = 100;
      setCreateProgress((prev) => ({
        ...prev,
        ...mapCreateProgress({
          ...data,
          processing: false,
          status: "completed",
          percent: 100,
        }),
        percent: 100,
        processing: false,
        status: "completed",
        message: data.message || "Catalogue ready",
      }));
      const pdfUrl = resolvePdfUrl(data.pdf_file);
      if (pdfUrl) {
        window.open(pdfUrl, "_blank");
      }
      toast.success(data.message || "Catalogue generated successfully");
      setTimeout(() => {
        setProgressOpen(false);
        fetchCatalogueList(true);
      }, 900);
    },
    [stopProgressPolling, fetchCatalogueList]
  );

  const handleCreateProgressFailed = useCallback(
    (data) => {
      stopProgressPolling();
      setCreateProgress(
        mapCreateProgress({ ...data, processing: false, status: "failed" })
      );
      toast.error(data.message || "Catalogue generation failed");
      setTimeout(() => {
        setProgressOpen(false);
        fetchCatalogueList(true);
      }, 1200);
    },
    [stopProgressPolling, fetchCatalogueList]
  );

  const startCreateProgressPolling = useCallback(
    (brochureId, initialData = null) => {
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
        progressPollRef.current = null;
      }
      if (brochureId != null) {
        activeBrochureIdRef.current = brochureId;
      }
      if (initialData) {
        const mapped = mapCreateProgress(initialData);
        apiPercentRef.current = Math.max(
          apiPercentRef.current || 0,
          mapped.percent || 0
        );
        setCreateProgress((prev) => {
          const nextPercent = Math.max(prev.percent || 0, mapped.percent || 0);
          return {
            ...prev,
            brochureId: mapped.brochureId ?? prev.brochureId,
            refNum: mapped.refNum || prev.refNum,
            totalParts: mapped.totalParts || prev.totalParts,
            totalProducts: mapped.totalProducts || prev.totalProducts,
            completedPart: mapped.completedPart || prev.completedPart,
            status: mapped.status || prev.status || "processing",
            pdfFile: mapped.pdfFile || prev.pdfFile,
            processing: true,
            percent: nextPercent,
            message: `${nextPercent}% completed`,
          };
        });
      }
      setProgressOpen(true);
      startProgressAnimation(false);

      const tick = async () => {
        try {
          const id = activeBrochureIdRef.current;
          if (id == null) return;
          const data = await fetchCreateCatalogueProgress(id);
          const mapped = mapCreateProgress(data);
          apiPercentRef.current = Math.max(
            apiPercentRef.current || 0,
            mapped.percent || 0
          );
          setCreateProgress((prev) => {
            const nextPercent = Math.max(
              prev.percent || 0,
              mapped.percent || 0,
              apiPercentRef.current || 0
            );
            const status = mapped.status || prev.status;
            const isDone = status === "completed" || !!mapped.pdfFile;
            const isFailed = status === "failed";
            return {
              ...prev,
              brochureId: mapped.brochureId ?? prev.brochureId,
              refNum: mapped.refNum || prev.refNum,
              totalParts: mapped.totalParts || prev.totalParts,
              totalProducts: mapped.totalProducts || prev.totalProducts,
              completedPart: mapped.completedPart || prev.completedPart,
              status: status || prev.status,
              pdfFile: mapped.pdfFile || prev.pdfFile,
              processing: isDone || isFailed ? false : true,
              percent: isDone ? 100 : nextPercent,
              message: isDone
                ? mapped.message || "Catalogue ready"
                : `${nextPercent}% completed`,
            };
          });

          const status = mapped.status;
          if (
            status === "completed" ||
            mapped.pdfFile ||
            (mapped.percent >= 100 && !mapped.processing)
          ) {
            handleCreateProgressCompleted(data);
            return;
          }
          if (status === "failed") {
            handleCreateProgressFailed(data);
          }
        } catch {
          void fetchCatalogueList(true);
        }
      };

      tick();
      progressPollRef.current = setInterval(tick, 2000);
    },
    [
      startProgressAnimation,
      fetchCreateCatalogueProgress,
      handleCreateProgressCompleted,
      handleCreateProgressFailed,
      fetchCatalogueList,
    ]
  );

  useEffect(() => {
    return () => {
      stopProgressPolling();
    };
  }, [stopProgressPolling]);

  const fetchProductList = async () => {
    setProductOptions([]);
    const Url = `${config.API_URL}/get-product`;
    try {
      const response = await fetch(Url, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.map((item) => ({
        label: item.name + "(" + item.code + ")",
        value: item.id,
      }));
      setProductOptions(formattedOptions);
    } catch (error) {
      console.error("api fail error fetching products");
    }
  };

  const fetchCategoryList = async () => {
    setProductOptions([]);
    const Url = `${config.API_URL}/get-category`;
    try {
      const response = await fetch(Url, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      setProductOptions(formattedOptions);
    } catch (error) {
      console.error("api fail error fetching products category wise");
    }
  };

  const fetchBrandList = async () => {
    setProductOptions([]);
    const Url = `${config.API_URL}/get-brands`;
    try {
      const response = await fetch(Url, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token || ''}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      setProductOptions(formattedOptions);
    } catch (error) {
      console.error("api fail error fetching products category wise");
    }
  };

  const fetchPromotedProductList = async () => {
    setProductOptions([]);
    const Url = `${config.API_URL}/get-promoted-product`;
    try {
      const response = await fetch(Url, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      setProductOptions(formattedOptions);
    } catch (error) {
      console.error("api fail error fetching products category wise");
    }
  };

  useEffect(() => {
    if (accessToken === null && token === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchCatalogueList();
  }, [accessToken, token, table.page, table.rowsPerPage, fetchCatalogueList]);

  useEffect(() => {
    table.onChangePage(null, 0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setBrandList(allBrandList);
      return;
    }

    const searchTerm = debouncedSearch.toLowerCase().trim();
    const filtered = allBrandList.filter((item) => {
      const date = (item.date || "").toLowerCase();
      const refNum = (item.ref_num || "").toLowerCase();
      const pdfFile = (item.pdf_file || "").toLowerCase();

      return (
        date.includes(searchTerm) ||
        refNum.includes(searchTerm) ||
        pdfFile.includes(searchTerm)
      );
    });

    setBrandList(filtered);
  }, [debouncedSearch, allBrandList]);

  useEffect(() => {
    setHideProducts(true);
    if (productType === "products") {
      fetchProductList();
    } else if (productType === "categories") {
      fetchCategoryList();
    } else if (productType === "brands") {
      fetchBrandList();
    } else if (productType === "new_arrivals") {
      setProductOptions([]);
      setHideProducts(false);
    } else if (productType === "promoted") {
      fetchPromotedProductList();
    }
  }, [productType]);

  const changeProductType = (e) => {
    const value = e.target.value;
    setProductType(value);
  };

  const specialOptions = [
    { label: "Select All", value: "select_all" },
    { label: "Remove All", value: "remove_all" },
  ];

  const handleChange = (selected) => {
    const selectedOptions = selected || [];
    const lastSelected = selectedOptions[selectedOptions.length - 1];

    if (lastSelected?.value === "select_all") {
      setSelectedOptions(productOptions);
      setProductsSelected(productOptions);
    } else if (lastSelected?.value === "remove_all") {
      setSelectedOptions([]);
      setProductsSelected([]);
    } else {
      setSelectedOptions(selectedOptions);
      setProductsSelected(selectedOptions);
    }
  };


  const submitCatalogue = async () => {
    const form = document.getElementById("createCatalogue");
    if (!form) return;

    const productTypeValue = productType;
    const packsize = packSizeEnabled ? "1" : "0";
    const typePriceValue = form.querySelector('input[name="type_price"]:checked')?.value;
    const sizeOptionValue =
      form.querySelector('input[name="size_options"]:checked')?.value || "3";
    const dateValue = catalogueDate ? format(catalogueDate, "dd-MM-yyyy") : "";
    const bannerImageInput = form.querySelector('input[name="banner_image"]');
    const bannerImage = bannerImageInput?.files?.[0];

    const productValues = productsSelected.map((product) => product.value);

    if (!productTypeValue) {
      toast.warning("Please select Product Type.");
      return;
    }
    if (!typePriceValue) {
      toast.warning("Please select Price.");
      return;
    }
    if (!sizeOptionValue) {
      toast.warning("Please select Size.");
      return;
    }
    if (!bannerImage) {
      toast.warning("Please upload Front Page (A4).");
      return;
    }
    if (!dateValue) {
      toast.warning("Please select Catalogue Date.");
      return;
    }
    if (hideProducts && (!productsSelected || productsSelected.length === 0)) {
      toast.warning("Please select at least one Product.");
      return;
    }

    const submitData = new FormData();
    submitData.append("option", productTypeValue);
    submitData.append("packsize", packsize);
    submitData.append("type_price", typePriceValue);
    submitData.append("size_options", sizeOptionValue);
    submitData.append("date", dateValue);
    submitData.append("banner_image", bannerImage);

    if (hideProducts && productValues.length > 0) {
      submitData.append("productlist", productValues.join(","));
    }

    setIsSubmitting(true);
    setOpenModal(false);
    setCreateProgress({
      percent: 0,
      status: "processing",
      message: "0% completed",
      completedPart: 0,
      totalParts: 0,
      totalProducts: 0,
      refNum: "",
      brochureId: null,
      processing: true,
      pdfFile: null,
    });
    setProgressOpen(true);
    startProgressAnimation(true);

    const Url = `${config.API_URL}/create-catalogue_new`;

    try {
      const response = await fetch(Url, {
        method: "POST",
        headers: {
          authorization: `Bearer ${authToken}`,
        },
        body: submitData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to create catalogue";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          if (errorText) {
            errorMessage =
              errorText.length > 200
                ? errorText.substring(0, 200) + "..."
                : errorText;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data?.success) {
        throw new Error(data?.message || "Failed to create catalogue");
      }

      const mapped = mapCreateProgress(data);
      apiPercentRef.current = Math.max(
        apiPercentRef.current || 0,
        mapped.percent || 0
      );
      setCreateProgress((prev) => {
        const nextPercent = Math.max(prev.percent || 0, mapped.percent || 0);
        return {
          ...prev,
          brochureId: mapped.brochureId ?? prev.brochureId,
          refNum: mapped.refNum || prev.refNum,
          totalParts: mapped.totalParts || prev.totalParts,
          totalProducts: mapped.totalProducts || prev.totalProducts,
          completedPart: mapped.completedPart || prev.completedPart,
          status: mapped.status || prev.status || "processing",
          pdfFile: mapped.pdfFile || prev.pdfFile,
          processing: true,
          percent: nextPercent,
          message: `${nextPercent}% completed`,
        };
      });

      setProductType("");
      setSelectedOptions([]);
      setProductsSelected([]);
      setSelectedFileName("");
      setCatalogueDate(new Date());
      setPackSizeEnabled(true);

      if (
        mapped.status === "completed" ||
        mapped.pdfFile ||
        (mapped.percent >= 100 && !mapped.processing)
      ) {
        handleCreateProgressCompleted(data);
        void fetchCatalogueList(true);
        return;
      }

      if (mapped.status === "failed") {
        handleCreateProgressFailed(data);
        return;
      }

      if (mapped.brochureId != null || mapped.processing) {
        startCreateProgressPolling(mapped.brochureId, data);
      }

      void fetchCatalogueList(true);
    } catch (error) {
      stopProgressPolling();
      setProgressOpen(false);
      setOpenModal(true);
      toast.error(
        error.message ||
          "There was an error submitting the catalogue. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStatus = async (id, status) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("status", status);
    const apiUrl = `${config.API_URL}/update_catalogs_status`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to change status");
      }

      const data = await response.json();
      console.log(data);

      toast.success("Status changed successfully");
      fetchCatalogueList();
    } catch (error) {
      console.error("Error status change:", error);
      toast.error("Failed to change status");
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setProductType("");
    setSelectedOptions([]);
    setProductsSelected([]);
    setSelectedFileName("");
    setCatalogueDate(new Date());
    setPackSizeEnabled(true);
  };

  const startIndex = table.page * table.rowsPerPage;
  const endIndex = startIndex + table.rowsPerPage;
  const dataInPage = brandList.slice(startIndex, endIndex) || [];

  const columnWidths = {
    'Sr No.': '70px',
    'Date': '110px',
    'Reference No': '150px',
    'Catalogue Name': '200px',
    'Status': '120px',
    'Action': '150px',
  };

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py:1.2 }}>
          <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography className="page-title">
                Catalogue
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6}} gap={1} display="flex" justifyContent="flex-end">
              <TextField
                className="input-border"
                size="small"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                variant="outlined"
                fullWidth 
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchInput("")}
                          edge="end"
                        >
                          <Iconify icon="eva:close-fill" width={20} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
                <Button
                variant="contained"
                className="button-variant-contained"
                startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
                onClick={handleOpenModal}
                sx={{ width: { xs: '100%', md: '230px' } }}
              >
                Create Catalogue
              </Button>
            </Grid2>
          </Grid2>
         </Box> 
          <TableContainer
          >
            <Table
              size="medium"
              className="table-cell-border"
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="70px"
        
                  >
                    Sr No.
                  </TableCell>
                  <TableCell width="110px"
                  >
                    Date
                  </TableCell>
                  <TableCell width="150px"
                  >
                    Reference No
                  </TableCell>
                  <TableCell width="200px"
                  >
                    Catalogue Name
                  </TableCell>
                  <TableCell width="120px"
                    align="center"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    width="150px"
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : dataInPage.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                        No catalogue found. Create your first catalogue.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dataInPage.map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      className="table-cell-style"
               
                    >
                      <TableCell >
                        {table.page * table.rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell
                      
                      >
                        <Typography
                          sx={{
                            color: isNotToday(row.date) ? "#212b36" : "#212b36",
                            fontSize: "0.75rem",
                            fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                          }}
                        >
                          {formatListDate(row.date)}
                        </Typography>
                      </TableCell>
                      <TableCell
                      
                      >
                        <Typography sx={{ fontSize: "0.75rem", color: "#212b36", fontWeight: 500, fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                          {row.ref_num}
                        </Typography>
                      </TableCell>
                      <TableCell
                     
                      >
                        <Typography sx={{ fontSize: "0.75rem", color: "#212b36", fontWeight: 500, fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                          {row.pdf_file || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                    
                      >
                        <Box
                          component="label"
                          sx={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            width: 90,
                            height: 28,
                            borderRadius: 14,
                            bgcolor: row.status == "1"
                              ? "#F8991D"
                              : theme.palette.grey[300],
                            transition: "background-color 0.3s ease",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            component="input"
                            type="checkbox"
                            checked={row.status == "1"}
                            onChange={(e) =>
                              handleChangeStatus(row.id, e.target.checked ? 1 : 0)
                            }
                            sx={{
                              position: "absolute",
                              opacity: 0,
                              width: 0,
                              height: 0,
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: "white",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                              transition: "transform 0.3s ease",
                              transform: row.status == "1"
                                ? "translateX(62px)"
                                : "translateX(3px)",
                              top: "3px",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              left: row.status == "1" ? 6 : "auto",
                              right: row.status == "1" ? "auto" : 6,
                              fontWeight: 500,
                              fontSize: "0.65rem",
                              letterSpacing: "0.2px",
                              color: row.status == "1"
                                ? "#fff"
                                : theme.palette.grey[600],
                              transition: "all 0.3s ease",
                              zIndex: 1,
                              pointerEvents: "none",
                              textTransform: "uppercase",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.status == "1" ? "ACTIVE" : "INACTIVE"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                    
                      >
                        {row.status == "0" ? (
                          <Typography sx={{ color: "#637381", fontSize: "0.75rem", fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                            -
                          </Typography>
                        ) : !row.pdf_file ? (
                          isNotToday(row.date) ? (
                            <Typography sx={{ color: "#637381", fontSize: "0.75rem", fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                              -
                            </Typography>
                          ) : (
                            <Box sx={{ minWidth: 90, px: 1 }}>
                              <Typography
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {progressOpen &&
                                String(createProgress.brochureId) ===
                                  String(row.brochure_id ?? row.id)
                                  ? `${createProgress.percent}%`
                                  : "Generating..."}
                              </Typography>
                              <LinearProgress
                                variant={
                                  progressOpen &&
                                  String(createProgress.brochureId) ===
                                    String(row.brochure_id ?? row.id)
                                    ? "determinate"
                                    : "indeterminate"
                                }
                                value={
                                  progressOpen &&
                                  String(createProgress.brochureId) ===
                                    String(row.brochure_id ?? row.id)
                                    ? createProgress.percent
                                    : 0
                                }
                                sx={{ height: 6, borderRadius: 1 }}
                              />
                            </Box>
                          )
                        ) : (
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              component="a"
                              href={`${row.pdf_file}`}
                              target="_blank"
                              download
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                },
                              }}
                            >
                              <Iconify icon="eva:download-fill" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{pt:2}} display="flex" justifyContent="flex-end" alignItems="center">
            <TablePaginationCustom
              page={table.page}
              dense={table.dense}
              count={brandList.length}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
   

      <Dialog
        open={openModal}
        onClose={() => {
          if (!isSubmitting) {
            handleCloseModal();
          }
        }}
        disableEscapeKeyDown={isSubmitting}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: -1,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#212b36"}}>
              Create Catalogue
            </Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Iconify icon="mdi:close" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <form id="createCatalogue">
            <Box>
              <FormLabel component="legend" sx={{ mb: -0.5, fontWeight: 600, color: "#000" }}>
                Product Type <span style={{ color: "red" }}>*</span>
              </FormLabel>
              <RadioGroup
                name="option"
                value={productType}
                onChange={changeProductType}
                sx={{
                  mb: 1,
                  display: "flex",
                  flexDirection: "row",
                  "& .MuiFormControlLabel-label": { color: "#000" },
                }}
              >
                <FormControlLabel
                  value="products"
                  control={<Radio className="checkBox" />}
                  label="Products Wise"
                />
                <FormControlLabel
                  value="categories"
                  control={<Radio className="checkBox" />}
                  label="Categories Wise"
                />
                <FormControlLabel
                  value="brands"
                  control={<Radio className="checkBox" />}
                  label="Brands Wise"
                />
                <FormControlLabel
                  value="promoted"
                  control={<Radio className="checkBox" />}
                  label="Promoted Wise"
                />
                {/* <FormControlLabel
                  value="new_arrivals"
                  control={<Radio className="checkBox" />}
                  label="New Arrivals"
                /> */}
              </RadioGroup>

             

              <FormLabel component="legend" sx={{ fontWeight: 600, mb: -0.5, color: "#000" }}>
                Price <span style={{ color: "red" }}>*</span>
              </FormLabel>
              <RadioGroup
                name="type_price"
                sx={{
                  mb: 1,
                  display: "flex",
                  flexDirection: "row",
                  "& .MuiFormControlLabel-label": { color: "#000" },
                }}
              >
                <FormControlLabel
                  value="box_price"
                  control={<Radio className="checkBox" />}
                  label="Box Price"
                />
                <FormControlLabel
                  value="piece_price"
                  control={<Radio className="checkBox" />}
                  label="Piece Price"
                />
                <FormControlLabel
                  value="both"
                  control={<Radio className="checkBox" />}
                  label="Both"
                />
                <FormControlLabel
                  value="no_price"
                  control={<Radio className="checkBox" />}
                  label="No Price"
                />
              </RadioGroup>

              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 4,
                }}
              >
                <Box>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: -0.5, color: "#000" }}>
                    Page Size <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <RadioGroup
                    name="size_options"
                    defaultValue="3"
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      "& .MuiFormControlLabel-label": { color: "#000" },
                    }}
                  >
                    <FormControlLabel
                      value="3"
                      control={<Radio className="checkBox" />}
                      label="4 X 5"
                    />
                  </RadioGroup>
                </Box>
                <Box>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: -0.5, color: "#000" }}>
                    Product Size
                  </FormLabel>
                  <FormControlLabel
                    sx={{ color: "#000", "& .MuiFormControlLabel-label": { color: "#000" } }}
                    control={
                      <Switch
                        className="checkBox"
                        name="packsize"
                        checked={packSizeEnabled}
                        onChange={(e) => setPackSizeEnabled(e.target.checked)}
                        value="1"
                      />
                    }
                    label="Pack size"
                  />
                </Box>
              </Box>

              <Grid2 container spacing={2} sx={{ mb: 2 }}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormLabel component="legend" sx={{ fontWeight: 500, color: "#000", mb: 0.5 }}>
                    Catalogue Date <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      className="input-border"
                      format="dd-MM-yyyy"
                      value={catalogueDate}
                      onChange={(newValue) => {
                        setCatalogueDate(newValue || new Date());
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          required: true,
                          name: "date",
                          placeholder: "dd-mm-yyyy",
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f7fa",
                              "&:hover": {
                                backgroundColor: "#fff",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "#fff",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormLabel component="legend" sx={{ fontWeight: 500, color: "#000", mb: 0.5 }}>
                    Front Page (A4) <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Button
                    variant="outlined"
                    component="label"
                    size="medium"
                    fullWidth
                    startIcon={<Iconify icon="eva:upload-fill" width={18} />}
                    sx={{
                      textTransform: "none",
                      justifyContent: "flex-start",
                      borderRadius: "8px",
                      fontWeight: 500,
                      borderColor: "rgba(145, 158, 171, 0.32)",
                      color: "#000",
                      backgroundColor: "#fff",
                      minHeight: 40,
                      "&:hover": {
                        borderColor: "rgba(145, 158, 171, 0.48)",
                        backgroundColor: "rgba(145, 158, 171, 0.04)",
                      },
                    }}
                  >
                    {selectedFileName || "Upload Front Page (A4)"}
                    <input
                      type="file"
                      name="banner_image"
                      accept="image/*,.pdf"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setSelectedFileName(file.name);
                        } else {
                          setSelectedFileName("");
                        }
                      }}
                    />
                  </Button>
                </Grid2>
              </Grid2>

              {hideProducts && (
                <Box sx={{ mb: 0 }}>
                  <FormLabel component="legend" sx={{ fontWeight: 500, color: "#000" }}>
                    Select Product <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    
                    isMulti
                    options={[...specialOptions, ...productOptions]}
                    value={selectedOptions}
                    onChange={handleChange}
                    closeMenuOnSelect={false}
                    placeholder="Select products..."
                    noOptionsMessage={() => "No products available"}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        borderRadius: "8px",
                        boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
                        border: "1px solid rgba(145, 158, 171, 0.16)",
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "40px",
                        cursor: "pointer",
                        border: state.isFocused
                          ? "1px solid rgba(25, 118, 210, 0.5)"
                          : "1px solid rgba(145, 158, 171, 0.32)",
                        borderRadius: "8px",
                        backgroundColor: "#f5f7fa",
                        boxShadow: state.isFocused
                          ? "0 0 0 2px rgba(25, 118, 210, 0.08)"
                          : "none",
                        "&:hover": {
                          borderColor: "rgba(145, 158, 171, 0.48)",
                          backgroundColor: "#fff",
                        },
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        maxHeight: "150px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                        padding: "8px 12px",
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        margin: "4px",
                        borderRadius: "6px",
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: "#1976d2",
                        fontSize: "0.875rem",
                        padding: "4px 8px",
                      }),
                      multiValueRemove: (provided) => ({
                        ...provided,
                        color: "#1976d2",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.16)",
                          color: "#1976d2",
                        },
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        maxHeight: "200px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "4px",
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        borderRadius: "6px",
                        margin: "2px",
                        backgroundColor: state.isSelected
                          ? "rgba(25, 118, 210, 0.08)"
                          : state.isFocused
                            ? "rgba(145, 158, 171, 0.04)"
                            : "transparent",
                        color: state.isSelected ? "#1976d2" : "#212b36",
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.08)",
                        },
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#919eab",
                        fontSize: "0.875rem",
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: "#637381",
                        "&:hover": {
                          color: "#212b36",
                        },
                      }),
                    }}
                    menuPosition="absolute"
                    menuPlacement="bottom"
                    maxMenuHeight={200}
                    isSearchable={true}
                  />
                </Box>
              )}
            </Box>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
          variant="outlined"
          className="button-variant-outlined"
          onClick={handleCloseModal} 
          disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#F8991D",
              "&:hover": {
                backgroundColor: "#F8991D",
                boxShadow: "none",
              },
            }}
            onClick={submitCatalogue}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={16} /> : null
            }
          >
            {isSubmitting ? "Creating..." : "Create Catalogue"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={progressOpen}
        onClose={() => {
          if (!createProgress.processing) {
            setProgressOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Creating Catalogue
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#212b36" }}>
              {CREATE_PROGRESS_MESSAGE}
            </Typography>
            <Typography variant="body2" sx={{ color: "#000" }}>
              {createProgress.percent}% completed
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, createProgress.percent))}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "rgba(248, 153, 29, 0.18)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  backgroundColor: "#F8991D",
                },
              }}
            />
            {createProgress.refNum ? (
              <Typography variant="caption" sx={{ color: "#000" }}>
                Reference: {createProgress.refNum}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            className="button-variant-outlined"
            disabled={createProgress.processing}
            onClick={() => {
              stopProgressPolling();
              setProgressOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Catalogue;
